import {AdjacencyMap} from './GraphUtilConstants';

interface NodeAndPath {
  nodeId: string;
  path: Array<string>;
}

/**
 * Finds the first nodes on all paths from the provided starting node which match the specified criteria.
 *
 * The provided starting node will not be evaluated as the target.
 *
 * @param startingNodeId The ID of the node to start the search from
 * @param adjacencyMap The adjacency map representing the graph
 * @param matchingFunction A function which determines whether or not a specific node is a target
 * @returns A list of target nodes and the paths from the starting node to the target
 */
export default function findFirstMatchingNodeAlongEveryPath(
  startingNodeId: string,
  adjacencyMap: AdjacencyMap,
  matchingFunction: (nodeId: string) => boolean
): Array<NodeAndPath> {
  const stack: Array<NodeAndPath> = [{nodeId: startingNodeId, path: []}];
  const visitedNodeIds: Set<string> = new Set();
  const targetNodeAndPaths: Array<NodeAndPath> = [];
  let visitedStartingNode = false;

  while (stack.length > 0) {
    const {nodeId, path} = stack.pop()!;
    // We have to account for the case where the starting node is revisited along the path, but only once
    const isNodeRevisitedStartingNode = nodeId === startingNodeId && path.length > 0 && !visitedStartingNode;
    if (visitedNodeIds.has(nodeId) && !isNodeRevisitedStartingNode) {
      continue;
    }
    if (isNodeRevisitedStartingNode) {
      visitedStartingNode = true;
    }
    visitedNodeIds.add(nodeId);
    if (path.length > 0 && matchingFunction(nodeId)) {
      targetNodeAndPaths.push({nodeId, path});
      continue;
    }
    if (adjacencyMap[nodeId] == null) {
      continue;
    }
    for (const neighborNodeId of adjacencyMap[nodeId]) {
      stack.push({nodeId: neighborNodeId, path: [...path, nodeId]});
    }
  }
  return targetNodeAndPaths;
}
